import React, { useState } from 'react';
import type { GearKit, GearItem } from '@/types/GearConfig';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfigSection } from '@/components/config_forms/shared/ConfigSection';
import { Layers, Plus, Trash2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GearKitFormProps {
  kits: GearKit[];
  availableItems: GearItem[];
  onChange: (updated: GearKit[]) => void;
}

interface KitRowProps {
  kit: GearKit;
  availableItems: GearItem[];
  onChange: (updated: GearKit) => void;
  onDelete: () => void;
}

const KitRow: React.FC<KitRowProps> = ({ kit, availableItems, onChange, onDelete }) => {
  const selectedItems = availableItems.filter((item) => kit.item_ids.includes(item.id));
  const unselectedItems = availableItems.filter((item) => !kit.item_ids.includes(item.id));

  const handleAddItem = (itemId: string) => {
    onChange({ ...kit, item_ids: [...kit.item_ids, itemId] });
  };

  const handleRemoveItem = (itemId: string) => {
    onChange({ ...kit, item_ids: kit.item_ids.filter((id) => id !== itemId) });
  };

  return (
    <div className="flex flex-col gap-3 p-3 rounded border bg-surface-1">
      {/* Kit header: name + delete */}
      <div className="flex items-center justify-between gap-2">
        <input
          type="text"
          value={kit.name}
          onChange={(e) => onChange({ ...kit, name: e.target.value })}
          placeholder="Kit name"
          className="h-8 flex-1 rounded border bg-surface-2 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Included items as chips */}
      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">Included Items</Label>
        <div className="flex flex-wrap gap-1.5">
          {selectedItems.map((item) => (
            <Badge
              key={item.id}
              variant="secondary"
              className="text-xs gap-1 pr-1 cursor-pointer hover:bg-destructive/10"
              onClick={() => handleRemoveItem(item.id)}
            >
              {item.name || 'Unnamed'}
              <X className="h-3 w-3" />
            </Badge>
          ))}
          {unselectedItems.length > 0 && (
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) handleAddItem(e.target.value);
              }}
              className="h-6 rounded border bg-surface-2 px-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">+ Add item</option>
              {unselectedItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name || 'Unnamed'}
                </option>
              ))}
            </select>
          )}
          {selectedItems.length === 0 && unselectedItems.length === 0 && (
            <span className="text-xs text-muted-foreground italic">No gear items defined</span>
          )}
        </div>
      </div>

      {/* Override prices */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground w-12 shrink-0">Daily</Label>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">$</span>
            <input
              type="number"
              value={kit.override_price ?? ''}
              onChange={(e) =>
                onChange({ ...kit, override_price: e.target.value ? parseFloat(e.target.value) : undefined })
              }
              placeholder="Sum"
              className={cn(
                "h-8 w-full rounded border bg-surface-2 px-2 text-right text-xs font-mono",
                "focus:outline-none focus:ring-1 focus:ring-primary"
              )}
              step={0.01}
              min={0}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground w-14 shrink-0">Weekly</Label>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">$</span>
            <input
              type="number"
              value={kit.weekly_override_price ?? ''}
              onChange={(e) =>
                onChange({ ...kit, weekly_override_price: e.target.value ? parseFloat(e.target.value) : undefined })
              }
              placeholder="Sum"
              className={cn(
                "h-8 w-full rounded border bg-surface-2 px-2 text-right text-xs font-mono",
                "focus:outline-none focus:ring-1 focus:ring-primary"
              )}
              step={0.01}
              min={0}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const GearKitForm: React.FC<GearKitFormProps> = ({
  kits,
  availableItems,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleAddKit = () => {
    const newKit: GearKit = {
      id: crypto.randomUUID(),
      name: '',
      item_ids: [],
    };
    onChange([...kits, newKit]);
  };

  const handleKitChange = (index: number, updated: GearKit) => {
    const updated_kits = [...kits];
    updated_kits[index] = updated;
    onChange(updated_kits);
  };

  const handleKitDelete = (index: number) => {
    onChange(kits.filter((_, i) => i !== index));
  };

  return (
    <ConfigSection
      title="Kit Presets"
      description="Bundle gear items into kits with optional discount pricing"
      icon={Layers}
      iconClassName="text-primary"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <div className="space-y-3">
        {kits.map((kit, index) => (
          <KitRow
            key={kit.id}
            kit={kit}
            availableItems={availableItems}
            onChange={(updated) => handleKitChange(index, updated)}
            onDelete={() => handleKitDelete(index)}
          />
        ))}

        <Button
          variant="secondary"
          size="sm"
          onClick={handleAddKit}
          className="w-full"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Kit
        </Button>
      </div>
    </ConfigSection>
  );
};
